import { z } from "zod";
import { timestampSchema } from "./timestamp";

export const choreScheduleSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("daily") }),
  z.object({
    type: z.literal("weekly"),
    daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1),
  }),
  z.object({ type: z.literal("once"), dueDate: timestampSchema }),
]);
export type ChoreSchedule = z.infer<typeof choreScheduleSchema>;

// "family" = pomoc rodzinie (XP only, bez PLN, bez transaction)
// "extra" = zadanie dodatkowe (PLN po approve, tworzy earn transaction)
// "sport" = wyzwanie sportowe (XP only jak family, z licznikiem powtórzeń lub minut)
export const choreKindSchema = z.enum(["family", "extra", "sport"]);
export type ChoreKind = z.infer<typeof choreKindSchema>;

// Jednostka licznika dla kind="sport". reps = powtórzenia (pompki, brzuszki),
// minutes = czas trwania (bieg, rower, piłka, koszykówka).
export const sportUnitSchema = z.enum(["reps", "minutes"]);
export type SportUnit = z.infer<typeof sportUnitSchema>;

// Skala wysiłku/czasu — szacowana przez AI (Haiku) przy tworzeniu.
//   S = rutyna, ≤15 min (pojedyncza prosta czynność)
//   M = skupienie lub 15–45 min (np. odkurzanie, mycie łazienki)
//   L = fizycznie wymagające lub >45 min (np. koszenie trawy, mycie auta)
// XP skaluje się przez size×kind — patrz src/lib/utils/xp.ts.
// Default "M" = backward-compat dla chore'ów sprzed wprowadzenia pola.
export const choreSizeSchema = z.enum(["S", "M", "L"]);
export type ChoreSize = z.infer<typeof choreSizeSchema>;

// Refine wspólne dla chore i choreInput — żeby walidacja działała tak samo
// przy parse z bazy i przy formularzu.
const refineChoreShape = <
  T extends {
    kind: ChoreKind;
    rewardMinor: number;
    sportUnit?: SportUnit;
    sportTarget?: number;
  },
>(
  val: T,
  ctx: z.RefinementCtx,
) => {
  if (val.kind === "family" && val.rewardMinor !== 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["rewardMinor"],
      message: "Pomoc rodzinie nie ma nagrody pieniężnej (rewardMinor=0).",
    });
  }
  if (val.kind === "sport") {
    if (val.rewardMinor !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rewardMinor"],
        message: "Wyzwanie sportowe nie ma nagrody pieniężnej (rewardMinor=0).",
      });
    }
    if (val.sportUnit === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sportUnit"],
        message: "Wyzwanie sportowe wymaga jednostki (powtórzenia/minuty).",
      });
    }
    if (val.sportTarget === undefined || val.sportTarget <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sportTarget"],
        message: "Wyzwanie sportowe wymaga dodatniego celu.",
      });
    }
  } else {
    if (val.sportUnit !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sportUnit"],
        message: "sportUnit dozwolone tylko dla wyzwania sportowego.",
      });
    }
    if (val.sportTarget !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sportTarget"],
        message: "sportTarget dozwolone tylko dla wyzwania sportowego.",
      });
    }
  }
};

const choreBaseSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(80),
  description: z.string().max(500).optional(),
  icon: z.string().max(40).optional(),
  rewardMinor: z.number().int().min(0).max(1_000_000),
  // "family" = pomoc rodzinie (bez PLN, tylko XP). "extra" = z nagrodą PLN.
  // "sport" = wyzwanie sportowe (bez PLN, z licznikiem reps/minut).
  // Default "extra" zachowuje zgodność wsteczną dla starych chores bez pola.
  kind: choreKindSchema.default("extra"),
  // Aktywność poza ekranem (sport, książka, spacer, kreatywność, kontakt
  // z rodziną). Niezależne od kind — może być family+offline lub extra+offline.
  offline: z.boolean().default(false),
  // Skala wysiłku — AI szacuje (S=≤15min, M=15–45min, L=>45min). XP via getXP.
  // Default "M" zachowuje wsteczną zgodność z chore'ami sprzed wprowadzenia.
  size: choreSizeSchema.default("M"),
  // userId konkretnego dziecka lub "any" (kto chce, ten weźmie)
  assignedTo: z.string(),
  schedule: choreScheduleSchema,
  requiresApproval: z.boolean(),
  // Czy do completion wymagane jest zdjęcie-dowód.
  requiresPhoto: z.boolean().optional(),
  // UID dorosłego, który funduje to zadanie (płaci dziecku).
  // Domyślnie = createdBy. Może być inny rodzic / babcia / dziadek.
  // Backward-compat: brak pola = traktuj jako createdBy.
  fundedBy: z.string().optional(),
  // === Pola dla kind="sport" — wymagane gdy sport, zabronione w innych ===
  // Jednostka licznika: reps (powtórzenia) lub minutes (czas).
  sportUnit: sportUnitSchema.optional(),
  // Cel do osiągnięcia — liczba powtórzeń albo minut.
  sportTarget: z.number().int().positive().optional(),
  // Auto-progresja: jeśli dodatnia, co tydzień (>=7d od sportLastBumpAt)
  // sportTarget rośnie o tę wartość atomowo przy approve completion.
  sportAutoBumpBy: z.number().int().positive().optional(),
  // Serwer-side timestamp ostatniego auto-bumpa. Klient nie pisze.
  sportLastBumpAt: timestampSchema.optional(),
  active: z.boolean(),
  createdBy: z.string(),
  createdAt: timestampSchema,
});

export const choreSchema = choreBaseSchema.superRefine(refineChoreShape);
export type Chore = z.infer<typeof choreSchema>;

export const choreInputSchema = choreBaseSchema
  .omit({
    id: true,
    createdAt: true,
    createdBy: true,
  })
  .superRefine(refineChoreShape);
export type ChoreInput = z.infer<typeof choreInputSchema>;
